import { useSnackbar } from "notistack";
import { useCallback, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import {
  createCurriculum as createCurriculumService,
  updateCurriculumBasicInformation as updateCurriculumBasicInformationService,
  deleteCurriculum as deleteCurriculumService, 
  getCurriculum
} from "../../services/curriculum/curriculum.service";
import { validate } from "../../utils";
import { handlerErrorV2 } from "../../utils/errors";
import { changedCurriculum, updateCurriculumsList, updateCurriculumsListMethod } from "../../store/actions/curriculum.actions";
import isEmpty from 'lodash/isEmpty';

const useCurriculums = () => {
  const { enqueueSnackbar } = useSnackbar()

  const dispatch = useDispatch();
  const [isLoadingList, setIsLoadingList] = useState(false);
  const [errors, setErrors] = useState({});

  const fetchCurriculums = useCallback(async () => {
    setIsLoadingList(true);
    try {
      const response = await getCurriculum();
      dispatch(updateCurriculumsList(response?.data || []));
    } catch (error) {
      setErrors({ message: error.message || 'An error ocurred'});
    } finally {
      setIsLoadingList(false);
    }
  }, []);

  useEffect(() => {
    fetchCurriculums();
  }, []);

  const validateForm = (form) => {
    const fieldRules = {
      name: 'required',
      shortName: 'required|withoutSpaces'
    }

    const errors = validate(fieldRules, form)
    if (!isEmpty(errors)) {
      return { error: true, ...errors }
    }

    return { error: false }
  }

  const createCurriculum = async (form = {}) => {
    const result = validateForm(form);
    if (result.error) {
      return result
    };

    try {
      const response = await createCurriculumService(form);
      if (!response.data && response.error) {
        return { error: true, message: response.error.message || 'An error ocurred'}
      } else {
        dispatch(updateCurriculumsListMethod('POST', response.data));
        enqueueSnackbar('Curriculum created successfully', { variant: 'success' })
        return { error: false }
      }
    } catch (error) {
      const message = handlerErrorV2(error)
      return { error: true, message }
    }
  }

  const updateCurriculumBasicInformation = async (curriculumId, form = {}) => {
    const result = validateForm(form);
    if (result.error) {
      return result
    };

    try {
      const response = await updateCurriculumBasicInformationService(curriculumId, form);
      if (!response.data && response.error) {
        return { error: true, message: response.error.message || 'An error ocurred'}
      } else {
        dispatch(updateCurriculumsListMethod('PUT', response.data));
        dispatch(changedCurriculum());
        enqueueSnackbar('Curriculum updated successfully', { variant: 'success' })
        return { error: false }
      }
    } catch (error) {
      const message = handlerErrorV2(error)
      return { error: true, message }
    }
  }

  const deleteCurriculum = async (curriculumId) => {
    try {
      const response = await deleteCurriculumService(curriculumId);
      if (!response.data && response.error) {
        return { error: true, message: response.error.message || 'An error ocurred'}
      } else {
        dispatch(updateCurriculumsListMethod('DELETE', curriculumId));
        enqueueSnackbar('Curriculum deleted successfully', { variant: 'success' })
        return { error: false }
      }
    } catch (error) {
      const message = handlerErrorV2(error)
      return { error: true, message }
    }
  }

  return {
    errors,
    isLoadingList,
    createCurriculum,
    updateCurriculumBasicInformation,
    deleteCurriculum
  }
};

export default useCurriculums;